import { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";
import { NavLink } from "react-router-dom";

//Components
import {
    Grid,
    Typography,
    Container,
    Paper,
    Divider
} from "@mui/material";
import { Button } from "@gpstrackit/react-controls"
import ChipStatus from 'Components/ChipStatus';

//Views
import DataView from 'Components/DataView';
import CreateEditManufacturer from 'Modules/manufacturers/views/.components/createEditManufacturer';
import DeleteManufacturer from 'Modules/manufacturers/views/.components/deleteManufacturer';
import SyncCTCInformation from 'Modules/manufacturers/views/.components/syncCTCInformation';
import { NotificationManager } from 'Components/Notifications';



//Security
import { HasPermission } from "Core/security";

//Actions
import {
    manufacturerCreateEditOpenModal,
    manufacturerDeleteOpenModal,
    syncCTCInformationOpenModal
} from "Redux/actions"

const ManufacturersMain = (props) => {
    const { messages } = useIntl();
    const tableRef = useRef();
    const dispatch = useDispatch();

    const user = useSelector(state => state.securityRedux.user);
    const deleteManuOpenModal = useSelector(state => state.deleteManufacturerRedux.openModal);
    const createEditManuOpenModal = useSelector(state => state.createEditManufacturerRedux.openModal);
    const syncCTCInfoOpenModal = useSelector(state => state.syncCTCInformationRedux.openModal);
    const lang = useSelector(state => state.commonRedux.language);

    const [columns, setColumns] = useState([
        {
            key: "id",
            title: messages['manufacturer_main_id'],
            width: 25,
            show: true,
            sort: true,
            locked: true,
            resize: true,
            export: true,
            functionString: 'return row?.id',
            code: "manufacturer_main_id",
            render: (item) => {
                return <span>{item?.id}</span>
            }
        },
        {
            key: "name",
            field: "name",
            title: messages['manufacturer_main_name'],
            show: true,
            sort: true,
            locked: false,
            resize: true,
            export: true,
            functionString: 'return row?.name',
            code: "manufacturer_main_name",
            render: (item) => {
                return (
                    <NavLink to={`/app/manufacturers/overview/${item?.id}`} style={{ color: 'black' }}>
                        <span style={{ textDecoration: 'underline', cursor: 'pointer' }}>{item?.name}</span>
                    </NavLink>
                )
            }
        },
        {
            key: "active",
            field: "active",
            title: messages['manufacturer_main_status'],
            show: true,
            sort: true,
            locked: false,
            export: true,
            functionString: `
                let lang = "${lang}"
                if(lang === "en" && row.active) {
                    return "Active"
                }
                if(lang === "en" && !row.active) {
                    return "Inactive"
                }

                if(lang === "es" && row.active) {
                    return "Activo"
                }
                if(lang === "es" && !row.active) {
                    return "Inactivo"
                }
            `,
            code: "manufacturer_main_status",
            width: 150,
            render: (item) => {
                return (
                    <ChipStatus statusCode={item?.active ? 1 : 20} messages={messages} />
                )
            }
        },
    ])
    const [searchFields, setSearchFields] = useState(["name"]);
    const [openPanel, setOpenPanel] = useState(false);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        let currentColumns = columns.map((item) => ({
            ...item,
            title: messages[item.code],
            render: item.key === "active" ? (item) => {
                return (
                    <ChipStatus statusCode={item?.active ? 1 : 20} messages={messages} />
                )
            } : item.render,
            functionString: item.key === "active" ? `
                let lang = "${lang}"
                if(lang === "en" && row.active) {
                    return "Active"
                }
                if(lang === "en" && !row.active) {
                    return "Inactive"
                }

                if(lang === "es" && row.active) {
                    return "Activo"
                }
                if(lang === "es" && !row.active) {
                    return "Inactivo"
                }
            `
             : item.functionString
        }))
        setColumns([...currentColumns])
    },[lang])

    return (
        <Container maxWidth={false} className="container-view">
            <Paper elevation={0}>
                <Grid
                    container
                    direction="row"
                    justifyContent="flex-start"
                    alignItems="center"
                    style={{ padding: "14px 18px", borderBottom: "1px solid #e8e8e8", height: '95v' }}
                >
                    <Grid item xs={6}>
                        <Typography variant="h5" gutterBottom component="div">
                            {messages['manufacturer_main_title']} ({total})
                        </Typography>
                    </Grid>
                    <Grid item xs={6} className="main-view-buttons">
                        <Button
                            disabled={!HasPermission(user, "man", "c")}
                            className="btn-dark-blue"
                            onClick={(e) => {
                                dispatch(manufacturerCreateEditOpenModal(true))
                                e.preventDefault();
                            }}
                        >
                            {messages['manufacturer_main_add_manufacturer']}
                        </Button>
                    </Grid>
                    <div style={{ width: '100%', marginBottom: 10, marginTop: 5 }}>
                        <Divider style={{ backgroundColor: '#999999' }} />
                    </div>
                    <DataView
                        ref={tableRef}
                        exportName="manufacturers"
                        dinamicQuery
                        name="pm-manufacturers"
                        entity="manufacturers"
                        graphqlFunction="searchManufacturers"
                        graphqlSort="ManufacturerSortInput"
                        graphqlConditions="ManufacturerSearchInput"
                        customQuery="id name active"
                        columns={columns}
                        hideAllInPagination
                        asyncSelect
                        searchConditions={["name"]}
                        searchOptionValue="id"
                        searchOptionLabel="name"
                        onChangeConditions={(tags, conditions, total, newColumns, rows, sort) => {
                            let currentConditions = {}
                            let currentTags = {}
                            if (!conditions && tags) {
                                currentConditions = tableRef?.current?.conditions;
                                currentTags = tags;
                            } else {
                                currentConditions = conditions || [];
                                currentTags = currentConditions?.filter((x) => x?.tags)[0]?.tags || [];
                                if (currentTags && currentTags.length > 0) tableRef?.current?.setTags(currentTags);
                            }

                            currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'id' && item?.fields[0] !== 'name');

                            let tagsById = currentTags.filter((item) => typeof item.id === "number");
                            if (tagsById?.length > 0) {
                                //New Items
                                let currentIds = tagsById.map((item) => (item.id));
                                if (currentIds.length > 0) {
                                    currentConditions.push({ fields: ["id"], terms: currentIds, exact: true, tags: currentTags, conditionOperator: "OR" });
                                }
                            }
                            let tagsByLabel = currentTags.filter(item => typeof item.id === "string")
                            if (tagsByLabel.length > 0) {
                                let currentLabels = tagsByLabel.map((item) => (item.name))
                                if (currentLabels.length > 0) {
                                    currentConditions.push({ fields: searchFields, terms: currentLabels, exact: false, tags: currentTags, conditionOperator: "OR" })
                                }
                            }

                            if (!conditions && tags) tableRef?.current?.getData(null, null, null, currentConditions, newColumns);
                            else tableRef?.current?.getData(null, rows, sort, currentConditions);
                        }}
                        searchFields={searchFields}
                        topheight={200}
                        onChangeTotal={(total) => setTotal(total)}
                        onFilters={() => { setOpenPanel(!openPanel) }}
                        noSplit
                        onChangeColumns={(items) => setColumns(items)}
                        tableButtons={["export", 'orderColumn']}
                        noFilters
                        options={[
                            {
                                id: "edit-manufacturer",
                                label: messages['edit'],
                                single: true,
                                show: true,
                                disabled: !HasPermission(user, "man", "u"),
                                action: (items) => {
                                    dispatch(manufacturerCreateEditOpenModal(true, items[0]))
                                }
                            },
                            {
                                id: "delete-manufacturer",
                                label: messages['delete'],
                                single: true,
                                show: true,
                                disabled: !HasPermission(user, "man", "d"),
                                action: (items) => {
                                    dispatch(manufacturerDeleteOpenModal(true, items[0]))
                                }
                            }
                        ]}
                        menuOptions={(row) => {
                            let actions = [
                                messages['edit'],
                                messages['delete']
                            ]
                            if (row.name.toUpperCase() === "CALAMP") {
                                actions.push(messages['manufacturer_main_sync_ctc_information'])
                            }
                            return actions
                        }}
                        onClickMenu={(event, item) => {
                            if (event === messages['edit']) {
                                if (HasPermission(user, "man", "u")) {
                                    dispatch(manufacturerCreateEditOpenModal(true, item))
                                } else {
                                    NotificationManager.warning(
                                        messages['common_error_permission'],
                                        messages['manufacturer_edit_title'],
                                        5000,
                                    );
                                }
                            }
                            if (event === messages['delete']) {
                                if (HasPermission(user, "man", "d")) {
                                    dispatch(manufacturerDeleteOpenModal(true, item))
                                } else {
                                    NotificationManager.warning(
                                        messages['common_error_permission'],
                                        messages['manufacturer_delete_title'],
                                        5000,
                                    );
                                }
                            }
                            if (event === messages['manufacturer_main_sync_ctc_information']) {
                                dispatch(syncCTCInformationOpenModal(true))
                            }
                        }}
                    />
                </Grid>
            </Paper>
            {createEditManuOpenModal && <CreateEditManufacturer tableRef={tableRef} />}
            {deleteManuOpenModal && <DeleteManufacturer tableRef={tableRef} />}
            {syncCTCInfoOpenModal && <SyncCTCInformation />}
        </Container>
    )
}
export default ManufacturersMain;